import React, { FC, createContext, useContext } from 'react';
import { Button, List, Popover } from 'antd';
import { CloseOutlined, ExportOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import OWLink from '../OWLink';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getFieldTechContact, getFieldTechDisplayName } from '../../utils/FieldTechUtils';
import { AppointmentCard } from './AppointmentCard';

const BACK_TEXT = 'Back to service calendar';
// Hover-card rows. The name and avatar are on the header already, so they are not repeated here.
const TECHNICIAN_DETAILS = [
	{ label: 'Title', key: 'title' },
	{ label: 'Department', key: 'department' },
	{ label: 'Email', key: 'email' },
	{ label: 'Mobile', key: 'phoneMobile' },
	{ label: 'Work', key: 'phoneWork' },
];

const TechnicianDetailsCard: FC<{ technician: any }> = ({ technician }) => {
	const contact = getFieldTechContact(technician);
	const rows = TECHNICIAN_DETAILS.filter((detail) => nullSafeGet(detail.key, contact));
	if (rows.length === 0) {
		return null;
	}
	return (
		<div className="flex flex-col gap-1">
			{rows.map((detail) => (
				<div key={detail.key} className="flex flex-row items-baseline gap-2">
					<span className="footnoteregular text-character-secondary">{detail.label}</span>
					<span className="bodyregular text-character-primary">
						{nullSafeGet(detail.key, contact)}
					</span>
				</div>
			))}
		</div>
	);
};

interface TechnicianAvatarWithNameEmailProps {
	technician: any;
	avatarSize?: number | 'small' | 'default' | 'large';
	/** Rendered to the right of the name — the column header hangs its detail link there. */
	nameSuffix?: React.ReactNode;
}

// The subscriber rows on SupplierWorkOrdersDetailNotesPage, verbatim: antd's List.Item.Meta with a
// ContactAvatar, the contact's name as the title and the email as the description. Using
// List.Item.Meta rather than hand-rolled markup keeps the same DOM (ant-list-item-meta-avatar /
// -title / -description) and therefore antd's own typography.
export const TechnicianAvatarWithNameEmail: FC<TechnicianAvatarWithNameEmailProps> = ({
	technician,
	avatarSize,
	nameSuffix,
}) => (
	<List.Item.Meta
		// `min-w-0` on antd's own content box as well: without it that flex child keeps its
		// max-content width, so the name and email push the column wider instead of ellipsising.
		className="min-w-0 flex-1 items-center [&_.ant-list-item-meta-content]:min-w-0"
		avatar={
			<ContactAvatar
				hidePopover={true}
				placement="bottomRight"
				userType={ROLE_TYPES.SUPPLIER}
				size={avatarSize}
				contact={getFieldTechContact(technician)}
			/>
		}
		title={
			<span className="flex min-w-0 max-w-full flex-row items-center gap-1">
				<span className="min-w-0 truncate">{getFieldTechDisplayName(technician)}</span>
				{nameSuffix}
			</span>
		}
		description={
			<span className="block max-w-full truncate">{nullSafeGet('email', technician)}</span>
		}
	/>
);

interface TechnicianIdentityProps {
	technician: any;
	avatarSize: number;
	nameSuffix?: React.ReactNode;
	/**
	 * How the identity claims its width, which differs by the caller's flex direction. `flex-1` is
	 * right inside TechnicianColumnHeader's row, where the identity absorbs the leftover width.
	 * TechnicianColumnsNav lays its children out in a column, so `flex-1` there sizes the height and
	 * leaves the width shrink-to-fit — which resolves the inner List.Item.Meta's `flex-1 min-w-0` to
	 * 0px and collapses the email away. That caller passes `w-full` instead.
	 */
	widthClassName?: string;
}

const TechnicianIdentity: FC<TechnicianIdentityProps> = ({
	technician,
	avatarSize,
	nameSuffix,
	widthClassName = 'flex-1',
}) => (
	<Popover
		placement="bottomLeft"
		content={
			// Name and avatar are already on the header itself, so the card shows contact details only.
			<TechnicianDetailsCard technician={technician} />
		}
	>
		{/* Popover attaches its hover handlers and a ref to this child. List.Item.Meta isn't a
		    forwardRef component, so it gets a plain wrapper to bind to. */}
		<div className={`flex min-w-0 flex-row items-center ${widthClassName}`}>
			<TechnicianAvatarWithNameEmail
				technician={technician}
				avatarSize={avatarSize}
				nameSuffix={nameSuffix}
			/>
		</div>
	</Popover>
);

interface TechnicianColumnHeaderProps {
	technician: any;
	onRemove: (email: string) => void;
}

export const TechnicianColumnHeader: FC<TechnicianColumnHeaderProps> = ({
	technician,
	onRemove,
}) => {
	if (!technician) {
		return null;
	}
	const email = nullSafeGet('email', technician);
	const detailPath = `/supplier/fieldTechs/detail/${encodeURIComponent(email)}/details`;

	const handleRemove = () => onRemove(email);

	return (
		<div className="technicianColumnHeader flex h-full w-full flex-row items-center gap-2 py-2 pl-3 pr-1 text-left">
			<TechnicianIdentity
				technician={technician}
				avatarSize={32}
				nameSuffix={
					<OWLink
						to={detailPath}
						target="_blank"
						preserveBackLinkStates={true}
						backText={BACK_TEXT}
						className="flex-none"
					>
						<ExportOutlined className="text-supplier" />
					</OWLink>
				}
			/>
			<Button
				type="text"
				size="small"
				className="flex-none self-start"
				icon={<CloseOutlined />}
				onClick={handleRemove}
			/>
		</div>
	);
};

export interface TechnicianNavValue {
	technician?: any;
	onPrevious: () => void;
	onNext: () => void;
	previousDisabled?: boolean;
	nextDisabled?: boolean;
}

export const TechnicianNavContext = createContext<TechnicianNavValue>({
	onPrevious: () => undefined,
	onNext: () => undefined,
	previousDisabled: true,
	nextDisabled: true,
});

export const TechnicianColumnsNav: FC = () => {
	const { technician, onPrevious, onNext, previousDisabled, nextDisabled } =
		useContext(TechnicianNavContext);
	return (
		<div className="flex h-full w-full flex-col items-end justify-center gap-1 p-4">
			{technician ? (
				<TechnicianIdentity technician={technician} avatarSize={24} widthClassName="w-full" />
			) : (
				<span className="footnoteMedium uppercase text-character-secondary">Techs</span>
			)}
			<div className="flex flex-none flex-row items-center gap-1">
				<Button
					size="small"
					icon={<LeftOutlined />}
					disabled={previousDisabled}
					onClick={onPrevious}
				/>
				<Button size="small" icon={<RightOutlined />} disabled={nextDisabled} onClick={onNext} />
			</div>
		</div>
	);
};

export const CALENDAR_COMPONENTS = {
	event: AppointmentCard,
	timeGutterHeader: TechnicianColumnsNav,
};
